import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from 'react-router-dom';
import App from './App';
import ApiService from './ApiService';
import SalesDataUploadPage from './pages/salesData/SalesDataUploadPage';
import SalesDataUploadStatusPage from './pages/salesData/SalesDataUploadStatusPage';
import SalesDataUploadMapping from './pages/salesData/SalesDataUploadMapping';
import CRMDataUploadPage from './pages/CRMDataUpload';
import UserManagement from './pages/userManagement/UserManagement';
import UserEdit from './pages/userEdit/UserEdit';
import AmcWorkflowsPage from './pages/amc/workflows/AmcWorkflowsPage';
import AmcWorkflowEditPage from './pages/amc/workflows/AmcWorkflowEditPage';
import AmcInstanceListPage from './pages/amc/instances/AmcInstanceListPage';
import AmcInstanceEditPage from './pages/amc/instances/AmcInstanceEditPage';
import AttributionPage from './pages/attribution/AttributionPage';

import WorkflowsPage from './pages/amc/workflows/WorkflowsPage';
import WorkflowEditPage from './pages/amc/workflows/WorkflowEditPage';
import DealerListPage from './pages/dealers/DealerListPage';
import DealerEditPage from './pages/dealers/DealerEditPage';
import OfferListPage from './pages/offers/OfferListPage';
import QuickSightDashboardPage from './pages/analytics/QuickSightDashboardPage';
import DefaultDashboardPage from './pages/analytics/DefaultDashboardPage';
import ReportingDashboardPage from './pages/analytics/ReportingDashboardPage';
import AmcAttributionEdit from './pages/amcAttribution/AmcAttributionEdit';
import AgencyEditPage from './pages/agency/AgencyEditPage';
import JobStatusListPage from './pages/jobStatus/JobStatusListPage';
import AnalyticsApiPage from './pages/analytics/AnalyticsApiPage';
import ImportListPage from './pages/importexport/ImportListPage';
import AmcAttributionList from './pages/amcAttribution/AmcAttributionList';
import AgencyListPage from './pages/agency/AgencyListPage';
import { AccessRoute } from './components/AccessGuard';
import AccessDeniedPage from './pages/errors/AccessDeniedPage';
import NotFoundPage from './pages/errors/NotFoundPage';
import {
    AGENCY_ADVERTISERS,
    AGENCY_API_DOCS,
    AGENCY_ATTRIBUTION,
    AGENCY_CAMPAIGNS,
    AGENCY_CRM_UPLOAD,
    AGENCY_DATA,
    AGENCY_HAT,
    AGENCY_INSIGHTS,
    AGENCY_REPORTING,
    USER_GROUP_ADMIN,
    USER_GROUP_ADVERTISER,
    USER_GROUP_AGENCY,
} from './types/User';
import { ReleaseNoteEditorPage } from './pages/releaseNotes/ReleaseNoteEditorPage';
import { ReleaseNoteListPage } from './pages/releaseNotes/ReleaseNoteListPage';
import DataSetPage from './pages/amc/datasets/DataSetPage';
import CustomAudiencesPage from './pages/amc/customAudiences/CustomAudiencesPage';
import OperationsDashboardPage from './pages/operationsDashboard/OperationsDashboardPage';
import DspAudiencePage from './pages/dspAudience/DspAudiencePage';
import StyleGuide from './pages/styleguide/StyleGuide';
import CampaignManagerPage from './pages/campaignManager/CampaignManagerPage';
import CampaignPage from './pages/campaign/CampaignPage/CampaignPage';
import LegacyCampaignEditPage from './pages/campaignManager/campaignEdit/CampaignEditPage';
import CampaignDetailsPage from './pages/campaignManager/campaignDetailView/CampaignDetailsPage';
import PolkAudiencesPage from './pages/polkAudiences/PolkAudiencesPage';
import PolkAudiencesQueuePage from './pages/polkAudiences/PolkAudienceQueuePage';
import DataMappingPage from './pages/dataMapping/DataMappingPage';
import SalesInsights from './pages/insights/SalesInsights';
import AdTagPlacementPage from './pages/adTag/AdTagPlacementPage';
import CampaignCreatePage from './pages/campaign/CampaignCreatePage';
import CampaignEditPage from './pages/campaign/CampaignEditPage';
import ReleaseNoteArticlesPage from './pages/releaseNoteArticles/ReleaseNoteArticlesPage';
import CampaignBulkCreatePage from './pages/campaign/CampaignBulkCreatePage';
import { HatDashboardPage } from './pages/hat/HatDashboardPage';
import AdvertiserActivityPage from './pages/advertiserActivity/AdvertiserActivityPage';
import EmailSenderPage from './pages/emailSender/EmailSenderPage';

// import CampaignBulkCreatePage from './pages/campaign/CampaignBulkCreatePage';

function AppRoutes() {
    return createBrowserRouter(
        createRoutesFromElements(
            <Route path="/" element={<App />} errorElement={<App />}>
                <Route index element={<Navigate to="campaignManager" replace />} />

                <Route path="salesDataUploads" element={<Outlet />}>
                    <Route path="" element={<SalesDataUploadStatusPage />} />
                    <Route
                        path="uploadMapping/:salesDataUploadId"
                        element={<SalesDataUploadMapping />}
                        loader={async ({ request, params }) => {
                            try {
                                const response = await ApiService.getSalesDataUpload(
                                    parseInt(params.salesDataUploadId as string)
                                );
                                return response.data;
                            } catch (error) {}

                            return undefined;
                        }}
                        errorElement={<NotFoundPage />}
                    />
                </Route>

                <Route path="crmDataUpload" element={<CRMDataUploadPage />} />

                <Route path="crmUpload" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CRM_UPLOAD}
                            >
                                <SalesDataUploadPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":advertiserId"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CRM_UPLOAD}
                            >
                                <SalesDataUploadPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="crmUploadResults" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CRM_UPLOAD}
                            >
                                <SalesDataUploadPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":salesDataUploadId"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CRM_UPLOAD}
                            >
                                <SalesDataUploadPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="users" element={<Outlet />}>
                    <Route
                        path=""
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <UserManagement />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":id"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <UserEdit />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="amcworkflows" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <AmcWorkflowsPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":workflowId"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <AmcWorkflowEditPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path="new"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <AmcWorkflowEditPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="amcInstances" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_DATA}
                            >
                                <AmcInstanceListPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":id"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_DATA}
                            >
                                <AmcInstanceEditPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="attribution" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessAgency={AGENCY_ATTRIBUTION}>
                                <AttributionPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route
                    path="hat"
                    element={
                        <AccessRoute accessAgency={AGENCY_HAT}>
                            <Outlet />
                        </AccessRoute>
                    }
                >
                    <Route index element={<Navigate to="all-media" replace />} />
                    <Route path=":tab" element={<HatDashboardPage />} />
                </Route>

                <Route path="adServing" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <AdTagPlacementPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="amcDataSets" element={<DataSetPage />} />

                <Route path="customAudiences" element={<CustomAudiencesPage />} />

                <Route path="workflows" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <WorkflowsPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":workflowId"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <WorkflowEditPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="/audiences" element={<DataSetPage />} />

                <Route path="/dspAudiences" element={<DspAudiencePage />} />

                <Route
                    path="advertisers"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                            accessAgency={AGENCY_ADVERTISERS}
                        >
                            <Outlet />
                        </AccessRoute>
                    }
                >
                    <Route index element={<DealerListPage />} />
                    {/* <Route path=":id?" element={<DealerEditPage />} /> */}

                    {/* ADD NEW ADVERTISER */}
                    <Route
                        path="new"
                        element={
                            <AccessRoute accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <DealerEditPage />
                            </AccessRoute>
                        }
                    />

                    {/* EDIT EXISTING ADVERTISER */}
                    <Route
                        path=":id"
                        element={
                            <AccessRoute accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <Outlet />
                            </AccessRoute>
                        }
                    >
                        <Route index element={<Navigate to="settings" replace />} />

                        <Route path=":tab" element={<DealerEditPage />} />
                    </Route>
                </Route>

                <Route
                    path="/opsDashboard"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <OperationsDashboardPage />
                        </AccessRoute>
                    }
                />

                <Route
                    path="/polkAudiences"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <PolkAudiencesPage />
                        </AccessRoute>
                    }
                />

                <Route
                    path="/polkAudiences/queue"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <PolkAudiencesQueuePage />
                        </AccessRoute>
                    }
                />

                <Route
                    path="tradeInOffers/:dealerId"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            condition={(uc) => uc.isAdmin() || uc.isOpsAdmin()}
                        >
                            <OfferListPage />
                        </AccessRoute>
                    }
                />
                <Route
                    path="quicksight"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                            accessAgency={AGENCY_REPORTING}
                        >
                            <QuickSightDashboardPage />
                        </AccessRoute>
                    }
                />
                <Route
                    path="analyticsSettings"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <DefaultDashboardPage />
                        </AccessRoute>
                    }
                />
                <Route
                    path="analyticsapi"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            accessAgency={AGENCY_API_DOCS}
                        >
                            <AnalyticsApiPage />
                        </AccessRoute>
                    }
                />

                <Route
                    path="reporting"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                            accessAgency={AGENCY_REPORTING}
                        >
                            <Outlet />
                        </AccessRoute>
                    }
                >
                    <Route index element={<Navigate to="all-media" replace />} />
                    <Route path=":tab" element={<ReportingDashboardPage />} />
                </Route>

                <Route
                    path="jobStatus"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <JobStatusListPage />
                        </AccessRoute>
                    }
                />

                <Route path="release-notes" element={<ReleaseNoteArticlesPage />} />

                <Route path="campaignManager" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <CampaignManagerPage />
                            </AccessRoute>
                        }
                    />

                    {/* HANDLE TAB NAVIGATION */}
                    <Route
                        path=":id"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <Outlet />
                            </AccessRoute>
                        }
                    >
                        {/* REDIRECTS TO /TAB=PERF */}
                        <Route index element={<Navigate to="performance" replace />} />

                        {/* HANDLES ALL TABBED PATHS */}
                        <Route path=":tab" element={<CampaignDetailsPage />} />
                    </Route>
                </Route>

                <Route path="campaign" element={<Outlet />}>
                    <Route
                        path=":id"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <Outlet />
                            </AccessRoute>
                        }
                    >
                        <Route index element={<Navigate to="overview" replace />} />
                        <Route path=":tab" element={<CampaignPage />} />
                    </Route>
                </Route>

                <Route path="legacy/campaign" element={<Outlet />}>
                    <Route
                        path="create"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <LegacyCampaignEditPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":id?"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <LegacyCampaignEditPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route path="campaign/v2" element={<Outlet />}>
                    <Route
                        path="create"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <CampaignCreatePage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":id?"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <CampaignEditPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path="bulkcreate"
                        element={
                            <AccessRoute
                                accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_ADVERTISER},${USER_GROUP_AGENCY}`}
                                accessAgency={AGENCY_CAMPAIGNS}
                            >
                                <CampaignBulkCreatePage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route
                    path="importManager"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <ImportListPage />
                        </AccessRoute>
                    }
                />

                <Route path="agencies" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <AgencyListPage />
                            </AccessRoute>
                        }
                    />

                    {/* HANDLE NEW AGENCY */}
                    <Route
                        path="new"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <AgencyEditPage />
                            </AccessRoute>
                        }
                    ></Route>

                    {/* HANDLE EXISTING AGENCY */}
                    <Route
                        path=":id"
                        element={
                            <AccessRoute accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}>
                                <Outlet />
                            </AccessRoute>
                        }
                    >
                        <Route index element={<Navigate to="agency-settings" replace />} />

                        <Route path=":tab" element={<AgencyEditPage />} />
                    </Route>
                </Route>

                {/* CHANGED URL FROM OLD PATH - SAFEGUARD FOR INTERIM */}
                <Route path="amcAttributionList" element={<Navigate to="/amcAttributionResults" replace />} />

                <Route path="amcAttributionResults" element={<Outlet />}>
                    <Route index element={<AmcAttributionList />} />
                    <Route path=":id?" element={<AmcAttributionEdit />} />
                </Route>

                <Route path="releaseNotes" element={<Outlet />}>
                    <Route
                        index
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <ReleaseNoteListPage />
                            </AccessRoute>
                        }
                    />
                    <Route
                        path=":id?"
                        element={
                            <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                                <ReleaseNoteEditorPage />
                            </AccessRoute>
                        }
                    />
                </Route>

                <Route
                    path="/styleguide"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <StyleGuide />
                        </AccessRoute>
                    }
                />

                <Route
                    path="insights"
                    element={
                        <AccessRoute
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            accessAgency={AGENCY_INSIGHTS}
                        >
                            <SalesInsights />
                        </AccessRoute>
                    }
                />

                <Route path="/dataMapping" element={<Navigate to="/dataMapping/ad-groups" replace />} />
                <Route path="/dataMapping/:tab" element={<DataMappingPage />} />

                <Route
                    path="advertiserActivity"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <AdvertiserActivityPage />
                        </AccessRoute>
                    }
                />

                {/* Hidden admin utility — no nav link, access by direct URL only */}
                <Route
                    path="/emailSender"
                    element={
                        <AccessRoute accessGroup={USER_GROUP_ADMIN}>
                            <EmailSenderPage />
                        </AccessRoute>
                    }
                />

                <Route path="/accessDenied" element={<AccessDeniedPage />} />
                <Route path="/notFound" element={<NotFoundPage />} />
                <Route path="*" element={<NotFoundPage />} />
            </Route>
        ),
        {
            future: {
                v7_relativeSplatPath: true,
            },
        }
    );
}

export default AppRoutes;
